import { type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

type Variant = "default" | "active" | "ink" | "lime" | "danger" | "outline";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: "sm" | "md";
  icon?: ReactNode;
};

const variantClass: Record<Variant, string> = {
  default: "bg-ink-6 text-ink-60 hover:text-ink",
  active:  "bg-white text-ink shadow-sm",
  ink:     "bg-ink text-white",
  lime:    "bg-lime text-[var(--fg-on-accent)] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]",
  danger:  "bg-danger/15 text-danger",
  outline: "border border-ink-12 text-ink-60 hover:text-ink hover:border-ink/40",
};

const sizeClass: Record<"sm" | "md", string> = {
  sm: "h-7 px-2.5 text-[12px]",
  md: "h-8 px-3 text-[13px]",
};

/**
 * Compact pill — used for filter chips, segmented buttons, status labels.
 * Replaces 15+ ad-hoc copies of `h-7/h-8 px-2.5 rounded-pill font-display
 * font-semibold` across the codebase.
 */
export function Pill({ variant = "default", size = "sm", icon, className, children, ...rest }: Props) {
  return (
    <button
      className={cn(
        "inline-flex items-center gap-1.5 rounded-pill font-display font-semibold whitespace-nowrap transition-colors",
        variantClass[variant],
        sizeClass[size],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
